<app-auth-form-card
    *ngIf="currentStep() === Step.Email || currentStep() === Step.Credentials"
    title="Sign in"
    subTitle="Enter your credentials to access the Management Portal or Care Circle">
    <ng-container *ngIf="redirectErrorMessage() as error">
        <cll-panel colour="error">
            <cll-icon cllPanelIcon>warning</cll-icon>

            {{ error.message }}

            <button (click)="redirectErrorMessage.set(undefined)"
                    cll-icon-button
                    cllPanelAction>
                <cll-icon>clear</cll-icon>
            </button>
        </cll-panel>
    </ng-container>

    <form (ngSubmit)="continue($event)">
        <div class="login-with space-y-16">
            <cll-form-field label="Email Address">
                <input
                    [formControl]="form.controls.email"
                    autocomplete="username current-email email"
                    autofocus="autofocus"
                    cllFormInput
                    tabindex="1"
                    data-testid="input:email"
                    type="email" />

                <button (click)="clearEmail()"
                        *ngIf="currentStep() === Step.Credentials"
                        cll-icon-button
                        cllSuffix>
                    <cll-icon>clear</cll-icon>
                </button>
            </cll-form-field>

            <ng-container *ngIf="!ssoEnabled">
                <ng-container *ngTemplateOutlet="passwordForm" />
            </ng-container>

            <button
                class="login-form-card__submit"
                cll-filled-button
                colour="secondary"
                type="submit"
                tabindex="4"
                *ngIf="currentStep() === Step.Email"
                data-testid="button:login"
                [disabled]="form.controls.email.invalid || (!ssoEnabled && form.controls.password.invalid) || isLoggingIn() === true">
                {{ ssoEnabled ? 'Continue' : 'Sign in' }}
                <cll-icon *ngIf="currentStep() !== Step.MfaChallenge">arrow_forward</cll-icon>
            </button>

            <ng-container *ngIf="currentStep() === Step.Credentials">
                <div *ngIf="ssoEnabled"
                     class="login__sso space-y-16">
                    <ng-container *ngIf="ssoClientsRequest$ | async as request">
                        <div *ngIf="request.isLoading"
                             style="text-align: center">
                            <cll-spinner />
                        </div>

                        <cll-panel *ngIf="request.error as error"
                                   colour="error">
                            <cll-icon cllPanelIcon>error_outline</cll-icon>

                            Unable to load SSO login options
                        </cll-panel>

                        <div *ngIf="request.value as response"
                             class="login__sso__clients space-y-16">

                            <ng-container *ngIf="response.clients.length > 0">
                                <a *ngFor="let client of response.clients"
                                   cll-outline-button
                                   [disabled]="ssoLaunched()"
                                   (click)="onLaunchedSingleSignOn()"
                                   [href]="client.redirect_uri">

                                    <div cllButtonIcon [ngSwitch]="client.type">
                                        <ng-container *ngSwitchCase="'google'">
                                            <ng-container *ngTemplateOutlet="googleIcon" />
                                        </ng-container>

                                        <ng-container *ngSwitchCase="'microsoft-entra'">
                                            <ng-container *ngTemplateOutlet="microsoftIcon" />
                                        </ng-container>

                                        <ng-container *ngSwitchCase="'microsoft-entra-oidc'">
                                            <ng-container *ngTemplateOutlet="microsoftIcon" />
                                        </ng-container>
                                    </div>

                                    {{ client.login_button_text }}
                                </a>

                                <div class="login-with__divider" *ngIf="showPasswordInput()">
                                    <div class="login-with__divider__line"></div>
                                    <div class="login-with__divider__text">or</div>
                                    <div class="login-with__divider__line"></div>
                                </div>
                            </ng-container>
                        </div>
                    </ng-container>
                </div>

                <div class="login-form" *ngIf="showPasswordInput()">
                    <ng-container *ngTemplateOutlet="passwordForm" />

                    <button
                        class="login-form-card__submit"
                        cll-filled-button
                        colour="secondary"
                        type="submit"
                        tabindex="4"
                        data-testid="button:login"
                        [disabled]="form.invalid || isLoggingIn() === true">
                        Sign in
                        <cll-icon *ngIf="currentStep() !== Step.MfaChallenge">arrow_forward</cll-icon>
                    </button>
                </div>
            </ng-container>
        </div>

        <ng-container *ngIf="loginRequest$ | async as request">
            <app-spinner *ngIf="request.isLoading" />

            <ng-container *ngIf="request.error as error">
                <app-alert-api-error [error]="error" />
            </ng-container>

            <ng-container *ngIf="request.value as response">
                <cll-panel
                    colour="info"
                    *ngIf="response.status === 'invalid_credentials'"
                    data-testid="alert:trouble-logging-in">
                    Trouble logging in?

                    <a
                        cll-button
                        cllPanelAction
                        [routerLink]="['/', 'auth', 'forgot-password']">
                        Reset your password?
                    </a>
                </cll-panel>

                <cll-panel
                    colour="error"
                    *ngIf="response.status === 'locked'"
                    data-testid="alert:account-locked">
                    <cll-icon cllPanelIcon>lock</cll-icon>
                    Your account has been locked. Please check your email for more information.
                </cll-panel>

                <cll-panel
                    colour="error"
                    *ngIf="response.status === 'expired'"
                    data-testid="alert:account-expired">
                    Your account has expired. Please contact your administrator.
                </cll-panel>
            </ng-container>
        </ng-container>
    </form>
</app-auth-form-card>

<app-auth-form-card
    *ngIf="currentStep() === Step.MfaChallenge"
    [title]="'Multi-factor authentication'"
    [subTitle]="' Provide a multi-factor authentication code from your Authenticator app.'">
    <cll-form-field label="MFA Code">
        <input
            cllFormInput
            type="text"
            [formControl]="form.controls.mfa_code"
            data-testid="input:mfa_code" />
    </cll-form-field>

    <p>
        If you're unable to use the Authenticator app, you can use one of your recovery codes or

        <a class="link"
           (click)="requestOtp()">
            request a one-time code via SMS
        </a>
    </p>

    <button
        class="login-form-card__submit"
        cll-filled-button
        colour="secondary"
        (click)="continue($event)"
        data-testid="button:login"
        [disabled]="form.invalid || isLoggingIn() === true">
        <cll-icon *ngIf="currentStep() === Step.MfaChallenge">check</cll-icon>
        Sign in
    </button>
</app-auth-form-card>

<a
    class="back-to-login"
    *ngIf="currentStep() === Step.MfaChallenge"
    (click)="backFromMfaChallenge()">
    <cll-icon>arrow_back</cll-icon>
    Back to login
</a>

<ng-container *ngIf="currentStep() === Step.Success">
    <app-auth-form-card
        data-testid="container:logged_in"
        [title]="'Logged in'">
        <cll-panel colour="primary" *ifCountry="Country.England">
            <cll-icon cllPanelIcon>info</cll-icon>
            By continuing, you acknowledge that the contents may contain Personal Confidential Data and that you are
            responsible for ensuring that you comply with the appropriate regulations.

            <br>

            <a
                href="https://digital.nhs.uk/data-and-information/looking-after-information/data-security-and-information-governance/nhs-and-social-care-data-off-shoring-and-the-use-of-public-cloud-services/cloud-risk-framework/dimensions-that-affect-risk#data-type"
                target="_blank">
                <strong>Official - Sensitive</strong>
            </a>

            information, which if disclosed unintentionally, could lead to moderate or long-term damage to
            individuals.
        </cll-panel>

        <a
            class="continue-button"
            colour="secondary"
            cll-filled-button
            data-testid="button:continue"
            [href]="successfulLoginResponse.redirect_to ?? '/'">
            <cll-icon>arrow_forward</cll-icon>
            Continue
        </a>
    </app-auth-form-card>

    <a
        class="cancel-button"
        data-testid="button:cancel"
        href="/auth/logout"
        target="_self">
        Cancel
    </a>
</ng-container>

<app-start-up-message
    (dismiss)="showStartUpMessage = false"
    *ngIf="showStartUpMessage" />

<ng-template #passwordForm>
    <cll-form-field label="Password">
        <input
            #password
            [formControl]="form.controls.password"
            autocomplete="password"
            cllFormInput
            tabindex="2"
            data-testid="input:password"
            type="password" />

        <cll-password-toggle [input]="password" cllSuffix />
    </cll-form-field>

    <div class="more-actions">
        <div>
            <cll-checkbox *ngIf="showRememberMe"
                          [formControl]="form.controls.remember"
                          data-testid="checkbox:remember-me"
                          tabindex="3">
                Remember me
            </cll-checkbox>
        </div>

        <p class="forgotten-password-container">
            <a
                class="forgotten-password-link"
                tabindex="-1"
                [routerLink]="['/', 'auth', 'forgot-password']"
                data-testid="link:forgotten-password">
                Forgotten password?
            </a>
        </p>
    </div>
</ng-template>

<ng-template #googleIcon>
    <svg fill="currentColor"
         height="16"
         style="vertical-align: middle"
         viewBox="0 0 16 16"
         width="16"
         xmlns="http://www.w3.org/2000/svg">
        <path
            d="M15.545 6.558a9.4 9.4 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.7 7.7 0 0 1 5.352 2.082l-2.284 2.284A4.35 4.35 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.8 4.8 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.7 3.7 0 0 0 1.599-2.431H8v-3.08z" />
    </svg>
</ng-template>

<ng-template #microsoftIcon>
    <svg fill="currentColor"
         height="16"
         style="vertical-align: middle"
         viewBox="0 0 16 16"
         width="16"
         xmlns="http://www.w3.org/2000/svg">
        <path d="M7.462 0H0v7.19h7.462zM16 0H8.538v7.19H16zM7.462 8.211H0V16h7.462zm8.538 0H8.538V16H16z" />
    </svg>
</ng-template>
